import { Divider, Flex } from "antd"
import { colors, spacings } from "themes/variables"
import IconWithBg from "ui/IconWithBg"
import { Text } from "ui/Typography"
import styles from "../index.module.css"
import moment from "moment"
import { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { getEventBorderColor, getEventColor, getJournalIcon, overlaps } from "../../../utils"
import BookingDetailsModal from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/Modals/BookingDetails"
import EventBlock from "../EventBlock"
import Journal from "../../../Modals/Journal"
import { useCalendarJournalNoteDates } from "../../../hooks/useCalendarSelectors"
import { useTimeOffForDate } from "../../../hooks/useTimeOff"
import { useRefreshCalendarBookings } from "../../../useRefreshCalendarBookings"
import useActiveGuide from "hooks/useActiveClient"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import { useDispatch, useSelector } from "react-redux"
import { getCalendarEquipment } from "store/calendar"
const HOUR_HEIGHT = 48
const ONE_MINUTE_MS = 60 * 1000

const getResourceEvents = (dayEvents, resource, isEquipmentTab, isCrewTab, resourceId, resourceLabel) => {
  if (isEquipmentTab) {
    return dayEvents.filter((ev) =>
      (ev.assigned_equipment || []).some((item) => item?.name === resource || item?.equipment_title === resource)
    )
  }
  if (isCrewTab) {
    if (resource === "Unassigned") return dayEvents.filter((ev) => !ev.assigned_crew || ev.assigned_crew.length === 0)
    return dayEvents.filter((ev) =>
      (ev.assigned_crew || []).some((item) => item?.name === resource || item?.full_name === resource)
    )
  }
  if (resourceId) return dayEvents.filter((ev) => ev.experience_id === resourceId)
  return dayEvents.filter((ev) => ev.resource === resourceLabel)
}

const buildOverlapClusters = (events) => {
  const sorted = [...events].sort((a, b) => (a.startMoment?.valueOf() ?? 0) - (b.startMoment?.valueOf() ?? 0))
  const clusters = []
  sorted.forEach((ev) => {
    const start = ev.startMoment?.valueOf() ?? 0
    const end = ev.endMoment?.valueOf() ?? start
    const hit = clusters.filter((c) => c.maxEnd > start + ONE_MINUTE_MS)
    if (hit.length === 0) {
      clusters.push({ events: [ev], maxEnd: end })
    } else {
      const merged = hit.reduce((acc, c) => {
        clusters.splice(clusters.indexOf(c), 1)
        acc.events.push(...c.events)
        acc.maxEnd = Math.max(acc.maxEnd, c.maxEnd)
        return acc
      }, { events: [ev], maxEnd: end })
      merged.maxEnd = Math.max(merged.maxEnd, end)
      clusters.push(merged)
    }
  })
  return clusters.map((c) => c.events)
}

const assignColumnsInCluster = (clusterEvents) => {
  const sorted = [...clusterEvents].sort((a, b) => (a.startMoment?.valueOf() ?? 0) - (b.startMoment?.valueOf() ?? 0))
  const colEnds = []
  const assignments = []
  sorted.forEach((ev) => {
    const startVal = ev.startMoment?.valueOf() ?? 0
    const endVal = ev.endMoment?.valueOf() ?? startVal
    let colIndex = colEnds.findIndex((colEnd) => colEnd + ONE_MINUTE_MS <= startVal)
    if (colIndex === -1) { colIndex = colEnds.length; colEnds.push(endVal) }
    else colEnds[colIndex] = endVal
    assignments.push({ ev, colIndex })
  })
  const totalCols = Math.max(colEnds.length, 1)
  return assignments.map(({ ev, colIndex }) => ({ ...ev, colIndex, totalCols }))
}

const computeEventColumns = (events) => buildOverlapClusters(events).flatMap(assignColumnsInCluster)

const getEventPosition = (ev, startHour) => {
  const { startMoment, endMoment } = ev
  if (!startMoment?.isValid() || !endMoment?.isValid()) return null
  const startMins = (startMoment.hour() - startHour) * 60 + startMoment.minute()
  const endMins = (endMoment.hour() - startHour) * 60 + endMoment.minute()
  const duration = Math.max(endMins - startMins, 30)
  return { top: Math.max(0, startMins) * (HOUR_HEIGHT / 60), height: duration * (HOUR_HEIGHT / 60) }
}

const DayGrid = ({ date, hours, events, resources: resourcesProp, profileIds, boatIds, activeTab, startHour, endHour }) => {
  const dispatch = useDispatch()
  const calendarCrew = useSelector((state) => state.staff?.calendarCrew || [])
  const isCrewTab = activeTab === 'crew';
  const isEquipmentTab = activeTab === 'equipment';
  const crewMembers = useMemo(() => {
    if (!isCrewTab) return []
    const active = calendarCrew.filter((c) => c.status?.toLowerCase() === "active")
    if (profileIds?.length > 0) {
      const filteredIds = profileIds.filter((id) => id !== "unassigned").map(String)
      if (filteredIds.length > 0) return active.filter((c) => filteredIds.includes(String(c.id)))
    }
    return active
  }, [calendarCrew, profileIds, isCrewTab])

  // Fetch equipment list when equipment tab is active
  const [equipmentList, setEquipmentList] = useState([]);
  useEffect(() => {
    if (!isEquipmentTab) return;
    dispatch(getCalendarEquipment())
      .then((res) => {
        const items = (res.data || []).map((eq) => ({ id: eq.id, name: eq.equipment_title }));
        setEquipmentList(items);
      })
      .catch(() => {});
  }, [dispatch, isEquipmentTab]);
  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false)
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)
  const displayDate = date ? moment(date).format("dddd, D MMMM") : ""
  const journalNoteDates = useCalendarJournalNoteDates()
  const { isActive: active } = useActiveGuide()
  const { hasPermission } = usePermissions()

  const handleJournalClick = () => {
    setIsJournalModalOpen(true)
  }

  const currentDay = moment(date).format("YYYY-MM-DD")
  const canViewJournal = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL)
  const hasJournalNote = journalNoteDates?.includes(currentDay)
  // Show icon if journal exists and user has view or edit permission, or if no journal and user has create permission
  const hasJournalPermission = hasJournalNote ? (canViewJournal || canEditJournal) : canCreateJournal
  // Convert event times to 24-hour format for matching
  const normalizedDayEvents = events.map((ev) => {
    const startMoment = ev.start ? moment(ev.start, ["h:mm A", "hh:mm A", "HH:mm"]) : null
    const endMoment = ev.end ? moment(ev.end, ["h:mm A", "hh:mm A", "HH:mm"]) : null
    return {
      ...ev,
      resource: ev.resource || ev.title || "Experience",
      startMoment,
      endMoment,
      startHour: startMoment?.isValid() ? startMoment.hour() : null,
      endHour: endMoment?.isValid() ? endMoment.hour() : null,
      startLabel: startMoment?.isValid() ? startMoment.format("h:mm A") : ev.start,
      endLabel: endMoment?.isValid() ? endMoment.format("h:mm A") : ev.end
    }
  })

  const dayEvents = normalizedDayEvents.filter((ev) => {
    if (moment(ev.date).format("YYYY-MM-DD") !== currentDay) return false
    if (ev.startHour === null) return true
    return ev.startHour >= startHour && ev.startHour < endHour
  })

  const resources = useMemo(() => {
    if (isCrewTab) {
      return ["Unassigned", ...crewMembers.map((c) => c.name)]
    }
    if (isEquipmentTab) {
      const list = equipmentList.map((eq) => eq.name)
      if (boatIds?.length > 0) return equipmentList.filter((eq) => boatIds.includes(eq.id)).map((eq) => eq.name)
      return list
    }
    const explicit = (resourcesProp || []).filter(Boolean)
    if (explicit.length) {
      // Deduplicate by id to preserve experiences with same title
      const encounteredIds = new Set()
      return explicit.filter((item) => {
        const key = item?.id ?? item
        if (encounteredIds.has(key)) return false
        encounteredIds.add(key)
        return true
      })
    }
    const names = normalizedDayEvents.map((ev) => ev.resource).filter(Boolean)
    const unique = Array.from(new Set(names))
    return unique.length ? unique : ["Experience"]
  }, [normalizedDayEvents, resourcesProp, isCrewTab, isEquipmentTab, equipmentList, crewMembers, boatIds])

  const columnCount = resources.length || 1
  const totalHeight = hours.length * HOUR_HEIGHT

  const MIN_LANE_WIDTH = 70
  const gridTemplateColumns = useMemo(() => {
    const resCols = resources.map((resource) => {
      const resourceId = typeof resource === "string" ? null : resource?.id
      const resourceLabel = typeof resource === "string" ? resource : resource?.title || resource?.name || resource?.trip_title || resource?.label
      const resourceEvents = getResourceEvents(dayEvents, resource, isEquipmentTab, isCrewTab, resourceId, resourceLabel)
      const valid = resourceEvents.filter((ev) => ev.startMoment?.isValid() && ev.endMoment?.isValid())
      const withCols = computeEventColumns(valid)
      const lanes = withCols.length > 0 ? Math.max(...withCols.map((e) => e.totalCols)) : 1
      const minW = Math.max(120, lanes * MIN_LANE_WIDTH)
      return `minmax(${minW}px, 1fr)`
    })
    return `52px 1px ${resCols.join(" ")}`
  }, [resources, dayEvents, isEquipmentTab, isCrewTab])

  const showCurrentTimeLine = moment(date).isSame(moment(), "day")
  const currentTimeOffset = useMemo(() => {
    if (!showCurrentTimeLine) return null
    const now = moment()
    const startMins = (now.hour() - startHour) * 60 + now.minute()
    return Math.max(0, startMins * (HOUR_HEIGHT / 60))
  }, [showCurrentTimeLine, startHour])

  const refreshCalendarBookings = useRefreshCalendarBookings()
  const handleBookingCancel = () => {
    setIsBookingModalOpen(false)
    setSelectedBookingId(null)
  }
  const handleBookingCancelSuccess = () => { handleBookingCancel(); refreshCalendarBookings() }

  const handleJournalCancel = () => {
    setIsJournalModalOpen(false)
  }

  // Centralized time-off info via hook
  const timeOffForDay = useTimeOffForDate(date)

  return (
    <>
      <Flex vertical className="overflow-auto" flex={1}>
        <Flex gap={spacings.XS} align="center" className="pt-m px-s">
          <Text color={colors.textSecondaryDark}>{displayDate} Journal</Text>
          {hasJournalPermission && (
            <IconWithBg
              icon={getJournalIcon(journalNoteDates, currentDay)}
              onClick={active ? handleJournalClick : undefined}
              disabled={!active}
            />
          )}
        </Flex>
        <Divider />
        <div key={`grid-${columnCount}`} className={styles.timeGridRoot}>
          {/* Header row */}
          <div className={styles.timeGridHeaderRow} style={{ gridTemplateColumns }}>
            <div className={styles.timeLabelHeader} />
            <div className={styles.timeDividerHeader} />
            {resources.map((resource) => {
              const label = typeof resource === "string" ? resource : resource?.title || resource?.name || resource?.trip_title || resource?.label
              const key = typeof resource === "string" ? resource : resource?.id ?? label
              return (
                <div key={key} className={styles.resourceHeaderCell}>
                  <Text fw="500">{label}</Text>
                </div>
              )
            })}
          </div>
          {/* Body: time labels + absolutely-positioned resource columns */}
          <div
            className={styles.timeGridTable}
            style={{
              display: "grid",
              gridTemplateRows: `repeat(${hours.length}, ${HOUR_HEIGHT}px)`,
              gridTemplateColumns,
              position: "relative"
            }}
          >
            {/* Current time line spans full width */}
            {showCurrentTimeLine && currentTimeOffset !== null && (
              <div
                className={styles.currentTimeLine}
                style={{ top: currentTimeOffset, gridColumn: `1 / -1`, position: "absolute", zIndex: 6 }}
              >
                <span className={styles.currentTimeDot} />
              </div>
            )}

            {/* Time labels */}
            {hours.map((hour, hIdx) => {
              const now = moment()
              const dayMoment = moment(date)
              const isPastHour = dayMoment.isBefore(now, "day") || (dayMoment.isSame(now, "day") && Number(hour) < now.hour())
              return (
                <div
                  key={`label-${hour}`}
                  className={`${styles.timeLabel} ${isPastHour ? styles.pastCalendarCell : ""}`}
                  style={{ gridRow: hIdx + 1, gridColumn: 1 }}
                >
                  <Text fw="500">{moment(hour, "H").format("h A")}</Text>
                </div>
              )
            })}

            {/* Divider column */}
            <div style={{ gridRow: `1 / span ${hours.length}`, gridColumn: 2, background: "var(--color-surface3)", width: 1 }} />

            {/* Resource columns */}
            {resources.map((resource, resIdx) => {
              const resourceId = typeof resource === "string" ? null : resource?.id
              const resourceLabel = typeof resource === "string" ? resource : resource?.title || resource?.name || resource?.trip_title || resource?.label
              const resourceKey = typeof resource === "string" ? resource : resource?.id ?? resourceLabel
              const resourceEvents = getResourceEvents(dayEvents, resource, isEquipmentTab, isCrewTab, resourceId, resourceLabel)
              const valid = resourceEvents.filter((ev) => ev.startMoment?.isValid() && ev.endMoment?.isValid())
              const eventsWithCols = computeEventColumns(valid)

              return (
                <div
                  key={resourceKey}
                  className={styles.dayColumn}
                  style={{
                    gridRow: `1 / span ${hours.length}`,
                    gridColumn: resIdx + 3,
                    height: totalHeight,
                    position: "relative"
                  }}
                >
                  {/* Hour divider lines */}
                  {hours.map((hour, hIdx) => {
                    const now = moment()
                    const dayMoment = moment(date)
                    const isPastHour = dayMoment.isBefore(now, "day") || (dayMoment.isSame(now, "day") && Number(hour) < now.hour())
                    return (
                      <div
                        key={`divider-${hour}`}
                        className={styles.hourDivider}
                        style={{ top: hIdx * HOUR_HEIGHT, ...(isPastHour ? { background: "var(--color-surface2)" } : {}) }}
                      />
                    )
                  })}

                  {/* Time-off overlays */}
                  {timeOffForDay?.allDay && <div className={`${styles.timeOffOverlay} ${styles.timeOffOverlayFull}`} />}
                  {!timeOffForDay?.allDay && (timeOffForDay?.ranges || []).map((toff) => {
                    const topPx = Math.max(0, (toff.start.hour() - startHour) * 60 + toff.start.minute()) * (HOUR_HEIGHT / 60)
                    const botPx = Math.max(0, (toff.end.hour() - startHour) * 60 + toff.end.minute()) * (HOUR_HEIGHT / 60)
                    return (
                      <div
                        key={`${currentDay}-${resourceKey}-${toff.start.format("HH:mm")}`}
                        className={styles.timeOffOverlay}
                        style={{ top: topPx, height: Math.max(0, botPx - topPx) }}
                      />
                    )
                  })}

                  {/* Event blocks absolutely positioned by time */}
                  {eventsWithCols.map((ev) => {
                    const pos = getEventPosition(ev, startHour)
                    if (!pos) return null
                    const colW = 100 / ev.totalCols
                    const conflict =
                      timeOffForDay?.allDay ||
                      (timeOffForDay?.ranges || []).some((r) => overlaps(ev.startMoment, ev.endMoment, r.start, r.end))
                    return (
                      <div
                        key={ev.id ?? ev.title + ev.start}
                        className={styles.eventBlockPositioned}
                        style={{
                          top: pos.top,
                          height: pos.height,
                          left: `${ev.colIndex * colW}%`,
                          width: `${colW}%`
                        }}
                      >
                        <EventBlock
                          event={ev}
                          eventColor={getEventColor(ev.payment_status)}
                          eventBorderColor={getEventBorderColor(ev.payment_status)}
                          conflict={conflict}
                          setIsBookingModalOpen={setIsBookingModalOpen}
                          setSelectedBookingId={setSelectedBookingId}
                          fullHeight
                        />
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </Flex>
      {isJournalModalOpen && (
        <Journal
          open={isJournalModalOpen}
          setOpen={setIsJournalModalOpen}
          onCancel={handleJournalCancel}
          selectedDate={date}
          startDate={date}
          endDate={date}
        />
      )}
      {isBookingModalOpen && (
        <BookingDetailsModal open={isBookingModalOpen} handleOnCancel={handleBookingCancel} bookingUuid={selectedBookingId} onCancelSuccess={handleBookingCancelSuccess} />
      )}
    </>
  )
}

DayGrid.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  hours: PropTypes.array.isRequired,
  events: PropTypes.array.isRequired,
  resources: PropTypes.array,
  activeTab: PropTypes.string,
  profileIds: PropTypes.array,
  boatIds: PropTypes.array,
  startHour: PropTypes.number,
  endHour: PropTypes.number,
}

export default DayGrid
